import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PAGE_SIZE } from '@catalog/const/v2';
import { ProductListFacade } from '@catalog/data-access';
import { Store } from '@ngrx/store';
import { CustomProduct, CustomProductScope } from '@product/const';
import { CustomPaginationModel } from '@shared/models';
import { WishlistEntry } from '@wishlist/const';
import { StateWithWishlist, WishlistActions, WishlistSelectors } from '@wishlist/data-access';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { WishlistService } from './wishlist.service';

@Injectable()
export class WishlistLazyService implements WishlistService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<StateWithWishlist>);
  private readonly productListFacade = inject(ProductListFacade);

  private wishlistResult$ = new BehaviorSubject<{ results: CustomProduct[]; pagination: CustomPaginationModel }>({
    results: [],
    pagination: { currentPage: 0, totalPages: 0 },
  });

  hasEntry(entry: WishlistEntry): Observable<boolean> {
    return this.store.select(WishlistSelectors.hasWishlistEntry(entry));
  }

  getEntryLoading(entry: WishlistEntry): Observable<boolean> {
    return combineLatest({
      isAddLoading: this.store.select(WishlistSelectors.getAddWishlistEntryLoading(entry)),
      isRemoveLoading: this.store.select(WishlistSelectors.getRemoveWishlistEntryLoading(entry)),
    }).pipe(map(({ isAddLoading, isRemoveLoading }) => isAddLoading || isRemoveLoading));
  }

  addEntry(entry: WishlistEntry): void {
    this.store.dispatch(new WishlistActions.AddWishlistEntry(entry));
  }

  removeEntry(entry: WishlistEntry): void {
    this.store.dispatch(new WishlistActions.RemoveWishlistEntry(entry));
  }

  getEntriesCount(): Observable<number> {
    return this.store.select(WishlistSelectors.getWishlistEntryListCount);
  }

  loadEntries(): void {
    this.store.dispatch(new WishlistActions.LoadWishlistEntryList());
  }

  getEntries(): Observable<WishlistEntry[]> {
    return this.store.select(WishlistSelectors.getWishlistEntryListValue);
  }

  getEntriesLoading(): Observable<boolean> {
    return this.store.select(WishlistSelectors.getWishlistEntryListLoading);
  }

  getEntriesError(): Observable<boolean> {
    return this.store.select(WishlistSelectors.getWishlistEntryListError);
  }

  getWishlist(): Observable<{ results: CustomProduct[]; pagination: CustomPaginationModel }> {
    return this.wishlistResult$.asObservable();
  }

  loadPage(
    page: number,
    entries: WishlistEntry[],
    onProductsLoaded?: (products: CustomProduct[], pageNum: number) => void,
  ): void {
    const startIndex = page * PAGE_SIZE;
    const entriesToLoad = entries.slice(startIndex, startIndex + PAGE_SIZE);

    if (entriesToLoad.length === 0) {
      return;
    }

    const productCodes = entriesToLoad.map(({ productCode }) => productCode);

    this.wishlistResult$.next({
      results: [],
      pagination: { currentPage: page, totalPages: 0 },
    });

    this.productListFacade
      .getProductsByCodes(productCodes, CustomProductScope.REFERENCE)
      .pipe(
        map((products) => {
          return productCodes
            .map((code) => products.find((p) => p.code === code))
            .filter((p): p is CustomProduct => Boolean(p));
        }),
        distinctUntilChanged((prev, curr) => {
          return prev.length === curr.length && prev.every((p, i) => p.code === curr[i].code);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((products) => {
        this.updateSearchResult(page, entries, products as CustomProduct[]);
        if (onProductsLoaded) {
          onProductsLoaded(products as CustomProduct[], page);
        }
      });
  }

  private updateSearchResult(page: number, entries: WishlistEntry[], products: CustomProduct[]): void {
    const totalPages = Math.ceil(entries.length / PAGE_SIZE);

    this.wishlistResult$.next({
      results: products,
      pagination: {
        currentPage: page,
        totalPages,
        totalCount: entries.length,
        hasNext: page < totalPages - 1,
        hasPrevious: page > 0,
      },
    });
  }

  loadProductsByEntries(entries: WishlistEntry[]): void {
    if (!entries.length) {
      return;
    }

    const productCodes = entries.map(({ productCode }) => productCode);
    this.productListFacade.loadProductsByCodes(productCodes, CustomProductScope.REFERENCE);
  }

  getProductsByEntries(entries: WishlistEntry[]): Observable<CustomProduct[]> {
    if (!entries.length) {
      return of([]);
    }

    const productCodes = entries.map(({ productCode }) => productCode);
    this.productListFacade.loadProductsByCodes(productCodes, CustomProductScope.REFERENCE);

    return this.productListFacade.getProductsByCodes(productCodes, CustomProductScope.REFERENCE).pipe(
      map((products) => {
        return productCodes
          .map((code) => products.find((p) => p.code === code))
          .filter((p): p is CustomProduct => Boolean(p));
      }),
      distinctUntilChanged((prev, curr) => {
        return prev.length === curr.length && prev.every((p, i) => p.code === curr[i].code);
      }),
    );
  }
}
